import React, { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Settings, Server, Database, Shield, Clock, Cloud, ArrowUpRight, Home, Lock, Unlock, Loader2, ChevronDown, FileText, Globe, ShieldAlert, Bug, RefreshCw, AlertTriangle, Info, AlertCircle, Zap, Download, UploadCloud, CheckCircle2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const LOCK_GROUPS = [
  {
    title: "Landing Page",
    items: [
      { key: "landing_become_member", label: "Become a Member" },
      { key: "landing_become_sponsor", label: "Become a Sponsor" },
      { key: "landing_follow_club", label: "Follow Club" },
      { key: "landing_apply_cta", label: "Apply CTA (header)" },
      { key: "landing_sponsor_cta", label: "Sponsor CTA (footer)" },
    ],
  },
  {
    title: "Members Portal",
    items: [
      { key: "member_fixtures", label: "Fixtures" },
      { key: "member_results", label: "Results" },
      { key: "member_stats", label: "Stats" },
      { key: "member_events", label: "Events" },
      { key: "member_gallery", label: "Gallery" },
      { key: "member_chat", label: "Chat" },
      { key: "member_on_loan", label: "On Loan" },
      { key: "member_profile", label: "Profile" },
    ],
  },
];

export default function SystemManagement() {
  const [locks, setLocks] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/feature-locks").then((r) => setLocks(r.data)).catch(() => {});
  }, []);

  const toggle = async (key) => {
    if (!locks) return;
    const next = { ...locks, [key]: !locks[key] };
    setLocks(next);
    setSaving(key);
    try {
      const { data } = await api.put("/feature-locks", next);
      setLocks(data);
    } catch {
      setLocks(locks);
    } finally {
      setSaving(null);
    }
  };

  // ── IT Data Log state ──
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logTab, setLogTab] = useState("network");
  const [logOpen, setLogOpen] = useState(false);

  // ── Deploy Data Seed state ──
  const [seedMeta, setSeedMeta] = useState(null);
  const [seedRefreshing, setSeedRefreshing] = useState(false);

  useEffect(() => {
    api.get("/admin/seed-status").then((r) => setSeedMeta(r.data)).catch(() => {});
  }, []);

  const refreshSeed = async () => {
    setSeedRefreshing(true);
    try {
      const { data } = await api.post("/admin/refresh-seed");
      if (data.status === "ok") {
        setSeedMeta(data);
        toast.success(`Deploy data refreshed — ${Object.values(data.counts || {}).reduce((a, b) => a + b, 0)} documents captured`);
      } else {
        toast.error(data.detail || "Seed refresh failed");
      }
    } catch {
      toast.error("Failed to refresh deploy data. Check your connection.");
    } finally {
      setSeedRefreshing(false);
    }
  };

  const fetchLogs = useCallback(async (category) => {
    setLogsLoading(true);
    try {
      const { data } = await api.get(`/admin/logs?category=${category}&limit=100`);
      setLogs(data.logs || []);
    } catch {
      setLogs([]);
    }
    setLogsLoading(false);
  }, []);

  const fetchLogStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/logs/stats");
      setLogStats(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (logOpen) {
      fetchLogs(logTab);
      fetchLogStats();
    }
  }, [logOpen, logTab, fetchLogs, fetchLogStats]);

  const refreshLogs = () => {
    fetchLogs(logTab);
    fetchLogStats();
  };

  const downloadFile = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    if (!logs.length) return;
    const label = TAB_META[logTab]?.label || logTab;
    const filename = `dcc-logs-${logTab}-${new Date().toISOString().slice(0, 10)}.json`;
    downloadFile(JSON.stringify(logs, null, 2), filename, "application/json");
    toast.success(`Exported ${logs.length} ${label} log entries as JSON`);
  };

  const exportCSV = () => {
    if (!logs.length) return;
    const label = TAB_META[logTab]?.label || logTab;
    const cols = ["timestamp", "level", "event", "message", "ip", "method", "path", "status_code", "duration_ms", "user_id", "member_id", "role", "error_code"];
    const escape = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.join(",");
    const rows = logs.map((e) => cols.map((c) => escape(e[c])).join(","));
    const csv = [header, ...rows].join("\n");
    const filename = `dcc-logs-${logTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadFile(csv, filename, "text/csv");
    toast.success(`Exported ${logs.length} ${label} log entries as CSV`);
  };

  const LEVEL_CONFIG = {
    info:     { color: "text-sky-500",    bg: "bg-sky-500/10 border-sky-500/20",   icon: Info },
    warn:     { color: "text-amber-500",  bg: "bg-amber-500/10 border-amber-500/20", icon: AlertTriangle },
    error:    { color: "text-red-500",    bg: "bg-red-500/10 border-red-500/20",   icon: AlertCircle },
    critical: { color: "text-red-700",    bg: "bg-red-700/10 border-red-700/20",   icon: Zap },
  };

  const TAB_META = {
    network:     { label: "Network",         icon: Globe,       key: "network" },
    access:      { label: "Access / Auth",   icon: ShieldAlert, key: "access" },
    application: { label: "Application",     icon: FileText,    key: "application" },
    debug:       { label: "Debug",           icon: Bug,         key: "debug" },
  };
  return (
    <AppShell variant="admin" title="System Management">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="admin-system-page">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
            Admin · System
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">System Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Platform settings, configuration and maintenance tools.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/a/system/general" className="group" data-testid="system-general-card">
            <Card className="p-5 rounded-2xl hover:border-foreground/40 transition-all hover-lift">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center group-hover:bg-[hsl(var(--accent))] group-hover:text-white transition-colors">
                  <Settings className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold tracking-tight">General Settings</h2>
                  <p className="text-xs text-muted-foreground">Club name, branding & contact info</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-[hsl(var(--accent))] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </div>
              <div className="text-sm text-muted-foreground">
                Configure core club details displayed across the platform.
              </div>
            </Card>
          </Link>



          <Collapsible defaultOpen={false}>
            <Card className="p-5 rounded-2xl" data-testid="system-security-card">
              <CollapsibleTrigger asChild>
                <button type="button" className="flex items-center gap-3 w-full text-left group cursor-pointer" data-testid="security-card-toggle">
                  <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold tracking-tight">Security & Access</h2>
                    <p className="text-xs text-muted-foreground">Lock or unlock buttons across the site</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
                <div className="mt-4">
                  {!locks ? (
                    <div className="flex items-center justify-center py-6 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading locks…
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {LOCK_GROUPS.map((group) => (
                        <div key={group.title}>
                          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-3">{group.title}</div>
                          <div className="space-y-2">
                            {group.items.map((item) => {
                              const enabled = locks[item.key] !== false;
                              return (
                                <div
                                  key={item.key}
                                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 hover:bg-muted/30 transition-colors"
                                  data-testid={`feature-lock-${item.key}`}
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    {enabled ? (
                                      <Unlock className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                                    ) : (
                                      <Lock className="h-3.5 w-3.5 text-red-500 shrink-0" />
                                    )}
                                    <span className={`text-sm truncate ${enabled ? "text-foreground" : "text-muted-foreground line-through"}`}>
                                      {item.label}
                                    </span>
                                  </div>
                                  <Switch
                                    checked={enabled}
                                    onCheckedChange={() => toggle(item.key)}
                                    disabled={saving === item.key}
                                    data-testid={`feature-lock-toggle-${item.key}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* IT Data Log */}
          <Collapsible open={logOpen} onOpenChange={setLogOpen}>
            <Card className="p-5 rounded-2xl" data-testid="system-itlog-card">
              <CollapsibleTrigger asChild>
                <button type="button" className="flex items-center gap-3 w-full text-left group cursor-pointer" data-testid="itlog-card-toggle">
                  <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center">
                    <Server className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold tracking-tight">IT Data Log</h2>
                    <p className="text-xs text-muted-foreground">Application, auth, network & debug logs</p>
                  </div>
                  {logStats && (
                    <Badge variant="outline" className="text-[10px] mr-2 tabular-nums">
                      {logStats.total?.toLocaleString()} entries
                    </Badge>
                  )}
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
                <div className="mt-4">
                  <Tabs value={logTab} onValueChange={setLogTab}>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <TabsList className="h-8">
                        {Object.entries(TAB_META).map(([key, meta]) => {
                          const catStats = logStats?.by_category?.[key] || {};
                          const errorCount = (catStats.error || 0) + (catStats.critical || 0) + (catStats.warn || 0);
                          return (
                            <TabsTrigger key={key} value={key} className="text-xs gap-1.5 px-2.5 h-7" data-testid={`itlog-tab-${key}`}>
                              <meta.icon className="h-3 w-3" />
                              {meta.label}
                              {errorCount > 0 && (
                                <span className="ml-0.5 text-[9px] bg-red-500/15 text-red-500 rounded-full px-1.5 leading-4">{errorCount}</span>
                              )}
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={exportCSV} disabled={!logs.length} data-testid="itlog-export-csv">
                          <Download className="h-3 w-3" /> CSV
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={exportJSON} disabled={!logs.length} data-testid="itlog-export-json">
                          <Download className="h-3 w-3" /> JSON
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={refreshLogs} data-testid="itlog-refresh">
                          <RefreshCw className={`h-3.5 w-3.5 ${logsLoading ? "animate-spin" : ""}`} />
                        </Button>
                      </div>
                    </div>

                    {Object.keys(TAB_META).map((tabKey) => (
                      <TabsContent key={tabKey} value={tabKey} className="mt-0">
                        {logsLoading ? (
                          <div className="flex items-center justify-center py-8 text-muted-foreground">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading logs…
                          </div>
                        ) : logs.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                            <FileText className="h-8 w-8 mb-2 opacity-40" />
                            <p className="text-sm">No {TAB_META[tabKey].label.toLowerCase()} logs recorded yet</p>
                            <p className="text-xs mt-1">Logs will appear here as events are captured</p>
                          </div>
                        ) : (
                          <ScrollArea className="h-[360px] rounded-lg border border-border">
                            <div className="divide-y divide-border">
                              {logs.map((entry) => {
                                const cfg = LEVEL_CONFIG[entry.level] || LEVEL_CONFIG.info;
                                const LevelIcon = cfg.icon;
                                const ts = new Date(entry.timestamp);
                                const timeStr = ts.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
                                const dateStr = ts.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
                                return (
                                  <div key={entry.id} className="px-3 py-2.5 hover:bg-muted/30 transition-colors text-xs" data-testid={`itlog-entry-${entry.id}`}>
                                    <div className="flex items-start gap-2">
                                      <LevelIcon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${cfg.color}`} />
                                      <div className="flex-1 min-w-0 space-y-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-muted-foreground tabular-nums whitespace-nowrap">{dateStr} {timeStr}</span>
                                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 border ${cfg.bg} ${cfg.color}`}>
                                            {entry.level}
                                          </Badge>
                                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-muted-foreground">
                                            {entry.event}
                                          </Badge>
                                          {entry.method && entry.status_code > 0 && (
                                            <span className="text-muted-foreground/70 whitespace-nowrap">
                                              {entry.method} {entry.status_code} <span className="text-muted-foreground/50">{entry.duration_ms > 0 ? `${entry.duration_ms}ms` : ""}</span>
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-foreground/80 break-all">{entry.message}</p>
                                        <div className="flex items-center gap-3 text-muted-foreground/60 flex-wrap">
                                          {entry.ip && <span>IP: {entry.ip}</span>}
                                          {entry.user_id && <span>User: {entry.user_id.slice(0, 8)}…</span>}
                                          {entry.member_id && <span>MID: {entry.member_id}</span>}
                                          {entry.role && <span>Role: {entry.role}</span>}
                                          {entry.error_code && <span className={cfg.color}>Code: {entry.error_code}</span>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          <Card className="p-5 rounded-2xl" data-testid="system-data-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-semibold tracking-tight">Data & Storage</h2>
                <p className="text-xs text-muted-foreground">Exports, backups & cleanup</p>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              Export club data, manage storage and run maintenance tasks.
            </div>
          </Card>

          <Card className="p-5 rounded-2xl" data-testid="system-deploy-data-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center">
                <UploadCloud className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold tracking-tight">Deploy Data</h2>
                <p className="text-xs text-muted-foreground">Sync content for publish</p>
              </div>
              {seedMeta?.generated_at && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 font-normal">
                  <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-500" />
                  Synced
                </Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground mb-3">
              Captures current settings, sponsors, events, and news so they deploy correctly when you publish.
              {seedMeta?.generated_at && (
                <span className="block mt-1.5 text-xs text-muted-foreground/70">
                  Last refreshed: {new Date(seedMeta.generated_at).toLocaleString()} · {seedMeta.counts ? Object.values(seedMeta.counts).reduce((a, b) => a + b, 0) : 0} docs · {seedMeta.compressed_bytes ? (seedMeta.compressed_bytes / 1024 / 1024).toFixed(1) : "?"} MB
                </span>
              )}
              {!seedMeta?.generated_at && seedMeta?.compressed_bytes > 0 && (
                <span className="block mt-1.5 text-xs text-muted-foreground/70">
                  Seed file exists ({(seedMeta.compressed_bytes / 1024 / 1024).toFixed(1)} MB) · auto-synced on edits
                </span>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={refreshSeed}
              disabled={seedRefreshing}
              data-testid="refresh-seed-btn"
              className="w-full"
            >
              {seedRefreshing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Refreshing…</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" /> Refresh Deploy Data</>
              )}
            </Button>
          </Card>

          <Link to="/a/playhq" className="group" data-testid="system-playhq-card">
            <Card className="p-5 rounded-2xl hover:border-foreground/40 transition-all hover-lift">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center group-hover:bg-[hsl(var(--accent))] group-hover:text-white transition-colors">
                  <Cloud className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold tracking-tight">PlayHQ</h2>
                  <p className="text-xs text-muted-foreground">Sync seasons & fixtures</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-[hsl(var(--accent))] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </div>
              <div className="text-sm text-muted-foreground">
                Manage PlayHQ integration, sync seasons, fixtures, ladders and team data.
              </div>
            </Card>
          </Link>

          <Card className="p-5 rounded-2xl" data-testid="system-scheduling-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-semibold tracking-tight">Scheduled Tasks</h2>
                <p className="text-xs text-muted-foreground">Automated jobs & reminders</p>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              View and manage automated cron jobs such as availability reminders and data syncs.
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
